import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, ArrowUpRight, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { AuthShell } from "@/layouts/AuthLayout";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const pwStrength = () => {
    if (password.length === 0) return null;
    if (password.length < 8) return "weak";
    if (/[A-Z]/.test(password) && /[0-9]/.test(password) && password.length >= 10) return "strong";
    return "moderate";
  };

  const strength = pwStrength();
  const strengthColor = { weak: "bg-red-400", moderate: "bg-amber-400", strong: "bg-emerald-500" }[strength] || "";
  const strengthWidth = { weak: "w-1/3", moderate: "w-2/3", strong: "w-full" }[strength] || "w-0";

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password reset successfully. Please log in.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-4">
        <Logo size="h-20" to="/" className="rounded-lg shadow-sm border border-[hsl(var(--zt-gold))]/20 p-1" />
        <p className="font-heading uppercase tracking-[0.3em] text-[10px] text-[hsl(var(--zt-teal))]/65 font-bold">
          Set New Password
        </p>
      </div>

      <h1 className="font-display text-4xl lg:text-5xl text-[hsl(var(--zt-teal))] leading-tight tracking-tight font-bold">
        Choose a new <em className="text-[hsl(var(--zt-gold))] not-italic gold-text-glow font-display">password</em>.
      </h1>

      {validating ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
        </div>
      ) : !tokenValid ? (
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 space-y-2">
            <XCircle size={20} className="text-red-500" />
            <p className="font-heading font-semibold text-red-800">Invalid or expired link</p>
            <p className="text-sm text-red-700 font-body">
              This password reset link is invalid or has already expired. Please request a new one.
            </p>
          </div>
          <Link to="/forgot-password"
            className="block w-full text-center bg-[hsl(var(--zt-gold))] text-white rounded-xl h-12 font-heading text-sm font-semibold tracking-tight leading-[3rem] elev-2">
            Request New Link
          </Link>
        </div>
      ) : done ? (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 space-y-2">
            <CheckCircle2 size={20} className="text-emerald-600" />
            <p className="font-heading font-semibold text-emerald-800">Password updated</p>
            <p className="text-sm text-emerald-700 font-body">
              Your password has been reset. Redirecting you to login…
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <label className="block space-y-2">
            <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">
              New Password <span className="text-[hsl(var(--zt-charcoal))]/50 normal-case tracking-normal font-medium">(8+ characters)</span>
            </span>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                autoFocus
                className="input-editorial has-affix font-body"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-lg text-[hsl(var(--zt-muted))] hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige))] transition-colors duration-200"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {strength && (
              <div className="space-y-1">
                <div className="h-1 w-full bg-[hsl(var(--zt-beige-deep))] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-300 ${strengthWidth} ${strengthColor}`} />
                </div>
                <p className={`text-[10px] font-heading uppercase tracking-wider ${
                  strength === "weak" ? "text-red-500" : strength === "strong" ? "text-emerald-600" : "text-amber-600"
                }`}>{strength} password</p>
              </div>
            )}
          </label>

          <label className="block space-y-2">
            <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">
              Confirm Password
            </span>
            <input
              type={showPw ? "text" : "password"}
              required
              className="input-editorial font-body"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your new password"
            />
            {confirm && password !== confirm && (
              <p className="text-[11px] text-red-500 font-heading">Passwords do not match</p>
            )}
          </label>

          <button
            type="submit"
            disabled={busy || password.length < 8 || password !== confirm}
            className="w-full bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-50 rounded-xl h-12 font-heading text-sm font-semibold tracking-tight flex items-center justify-center gap-2 elev-2 transition-all duration-200 hover:-translate-y-0.5 disabled:hover:translate-y-0"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpRight size={16} />}
            {busy ? "Resetting…" : "Reset Password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
